// The cards on Leads by Assignee: one per assignable user, one per other person who has leads, and
// an "Unassigned" card when some leads have nobody. Nothing here is a fixed roster or a made-up number.

export const UNASSIGNED = 'Unassigned'

export interface AssigneeLead {
  assignee?: string
  assignedToUserId?: string
  value?: number
  convertedDealId?: string
}

export interface AssigneeUser {
  id: string
  name: string
}

export interface AssigneeCard<L extends AssigneeLead> {
  name: string
  userId?: string
  leads: L[]
  count: number
  pipelineValue: number
  /** Share of this person's leads that became a deal, as a whole percent. */
  conversionPercent: number
}

const norm = (s: string | undefined | null) => String(s ?? '').trim().toLowerCase()

export function buildAssigneeCards<L extends AssigneeLead>(leads: L[], users: AssigneeUser[]): AssigneeCard<L>[] {
  const byId = new Map(users.map((u) => [u.id, u]))
  const keyOf = (lead: L): string => {
    if (lead.assignedToUserId && byId.has(lead.assignedToUserId)) return `id:${lead.assignedToUserId}`
    const name = norm(lead.assignee)
    return name ? `name:${name}` : 'none'
  }

  const groups = new Map<string, { name: string; userId?: string; leads: L[] }>()
  for (const u of users) {
    groups.set(`id:${u.id}`, { name: u.name, userId: u.id, leads: [] })
  }
  // A user whose name matches a lead's free-text assignee gets that lead too.
  const idByName = new Map(users.map((u) => [norm(u.name), u.id]))

  for (const lead of leads) {
    let key = keyOf(lead)
    if (key.startsWith('name:')) {
      const matchId = idByName.get(key.slice(5))
      if (matchId) key = `id:${matchId}`
    }
    if (!groups.has(key)) {
      groups.set(key, key === 'none'
        ? { name: UNASSIGNED, leads: [] }
        : { name: String(lead.assignee).trim(), leads: [] })
    }
    groups.get(key)!.leads.push(lead)
  }

  const cards: AssigneeCard<L>[] = []
  for (const [key, g] of Array.from(groups.entries())) {
    if (key === 'none' && g.leads.length === 0) continue
    const pipelineValue = g.leads.reduce((sum, l) => sum + (Number(l.value) || 0), 0)
    const converted = g.leads.filter((l) => Boolean(l.convertedDealId)).length
    cards.push({
      name: g.name,
      userId: g.userId,
      leads: g.leads,
      count: g.leads.length,
      pipelineValue,
      conversionPercent: g.leads.length > 0 ? Math.round((converted / g.leads.length) * 100) : 0,
    })
  }
  // Unassigned goes last; everyone else keeps the order users were given in.
  return cards.sort((a, b) => Number(a.name === UNASSIGNED) - Number(b.name === UNASSIGNED))
}
